import React, { FC, useEffect, useMemo, useState } from 'react'
import { BlackParagraph } from 'components/atoms/Paragraph'
import Table from 'components/organisms/Table'
import Header from 'components/organisms/Table/atoms/Header'
import { IconButton } from 'components/molecules/Button'
import Icon from '../../components/atoms/Icon'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useConfirm } from 'components/organisms/ConfirmDialog'
import { useQueryClient } from 'react-query'
import {
  Column,
  useExpanded,
  useFlexLayout,
  useGlobalFilter,
  usePagination,
  useResizeColumns,
  useSortBy,
  useTable,
} from 'react-table'
import { debounce } from 'lodash'
import { emptyArray } from 'utils/array'
import { highlight } from 'utils/highlight'
import { Dropdown } from 'components/molecules/Dropdown'
import { MenuItem, Popup } from 'components/molecules/Popup'
import { downloadLogFn, getDeviceLogsFn, deleteLogFn } from 'api'
import { Page, PageHeader } from 'components/organisms/layout'
import { useUserContext } from 'contexts/UserContext'
import dayjs from 'dayjs'
import { formatFileSize } from 'utils/formatFileSize'

interface DeviceLogRow {
  id: number
  username: string
  createdAt: string
  fileSize: number
  deviceId: number
  filename: string
  deviceos: string
  clientversion: string
}

const DownloadButton: FC<{ logEntry: DeviceLogRow }> = ({ logEntry }) => {
  const [downloading, setDownloading] = useState<boolean>(false)
  const [t] = useTranslation()

  const handleDownload = async () => {
    if (downloading) return

    try {
      setDownloading(true)
      await downloadLogFn(logEntry.deviceId, logEntry.id, logEntry.filename)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <MenuItem
      value="download"
      icon={downloading ? <Icon icon="uil:sync" className="spin" /> : <Icon icon="uil:arrow-circle-down" />}
      onClick={handleDownload}
      disabled={downloading}
    >
      <span style={{ marginRight: '10px' }}>{t('common.download')}</span>
    </MenuItem>
  )
}

const DeleteButton: FC<{ logEntry: DeviceLogRow }> = ({ logEntry }) => {
  const { confirm } = useConfirm()
  const queryClient = useQueryClient()
  const [t] = useTranslation()

  const handleDelete = async () => {
    const isConfirmed = await confirm(t('modal.deleteLog.confirmDelete'))

    if (!isConfirmed) return
    await deleteLogFn(logEntry.deviceId, logEntry.id)
    queryClient.invalidateQueries(['deviceLogs'])
  }

  return (
    <MenuItem value="delete" red icon={<Icon icon="uil:trash" />} onClick={handleDelete}>
      {t('common.delete')}
    </MenuItem>
  )
}

const getColumns = (): Column<DeviceLogRow>[] => [
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.user')} />
    },
    accessor: 'username',
    disableSortBy: true,
    Cell: ({ value, state: { globalFilter } }) => {
      return <BlackParagraph text={highlight(value, globalFilter.search)} size="12" />
    },
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.os')} />
    },
    accessor: 'deviceos',
    disableSortBy: true,
    Cell: ({ value, state: { globalFilter } }) => {
      return <BlackParagraph text={highlight(value, globalFilter.search)} size="12" />
    },
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.version')} />
    },
    accessor: 'clientversion',
    disableSortBy: true,
    Cell: ({ value, state: { globalFilter } }) => {
      return <BlackParagraph text={highlight(value, globalFilter.search)} size="12" />
    },
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.timeOfExport')} />
    },
    accessor: 'createdAt',
    disableSortBy: true,
    Cell: ({ value, state: { globalFilter } }) => {
      if (!value) return <div>-</div>
      return (
        <BlackParagraph text={highlight(dayjs(value).format('D MMMM YYYY, HH:mm'), globalFilter.search)} size="12" />
      )
    },
    width: 100,
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.fileSize')} />
    },
    accessor: 'fileSize',
    disableSortBy: true,
    Cell: ({ value, state: { globalFilter } }) => {
      return <BlackParagraph text={highlight(formatFileSize(Number(value)), globalFilter.search)} size="12" />
    },
    width: 40,
  },
  {
    Header: () => null,
    accessor: 'id',
    disableSortBy: true,
    maxWidth: 40,
    Cell: ({ row }: any) => {
      const log = row.original as DeviceLogRow
      return (
        <>
          <Dropdown
            button={
              <IconButton>
                <Icon icon="uil:ellipsis-v" />
              </IconButton>
            }
          >
            <Popup>
              <DownloadButton logEntry={log} />
              <DeleteButton logEntry={log} />
            </Popup>
          </Dropdown>
        </>
      )
    },
  },
]

const initialGlobalFilter = { feature_profile: [], search: '' }

const DeviceLogs: FC = () => {
  const [t] = useTranslation()
  const { state } = useUserContext()
  const { i_realm } = state?.realm
  const [pagination, setPagination] = useState(() => ({ pageIndex: 0, pageSize: 50 }))
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const columns = useMemo(() => getColumns(), [])

  const { data: devicelogs, isLoading } = useQuery(
    ['deviceLogs', i_realm, pagination, undefined, search],
    getDeviceLogsFn,
    { keepPreviousData: true }
  )

  const tableData = useMemo<DeviceLogRow[]>(() => {
    if (!devicelogs?.items) return emptyArray

    return devicelogs.items.map((log) => {
      const device = log.device
      const user = device?.customer?.user
      return {
        id: log.id,
        username: user?.user_id ?? '-',
        createdAt: log.created_at,
        fileSize: log.file_size,
        deviceId: log.i_device,
        filename: log.filename,
        deviceos: log.device?.deviceos ?? '-',
        clientversion: log.device?.clientversion ?? '-',
      }
    })
  }, [devicelogs])

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        tableProps.setGlobalFilter({
          ...tableProps.state.globalFilter,
          search: value,
        })
        setSearch(value)
      }, 300),
    //eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    return () => debouncedSetSearch.cancel()
  }, [debouncedSetSearch])

  const pageCount = useMemo(
    () => Math.ceil(((devicelogs?.total as number) || 0) / pagination.pageSize) || 0,
    [devicelogs?.total, pagination.pageSize]
  )

  const tableProps = useTable(
    {
      columns,
      data: tableData,
      initialState: { pageSize: pagination.pageSize, globalFilter: { ...initialGlobalFilter } },
      autoResetPage: false,
      disableSortRemove: true,
      manualPagination: true,
      manualSortBy: true,
      manualGlobalFilter: true,
      matablePropsnualGlobalFilter: true,
      pageCount: pageCount,
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useFlexLayout,
    useResizeColumns
  )

  useEffect(() => {
    const { pageIndex, pageSize } = tableProps.state
    if (pageIndex !== pagination.pageIndex || pageSize !== pagination.pageSize) {
      setPagination({ pageIndex, pageSize })
    }
  }, [tableProps.state, pagination])

  return (
    <Page title={t('page.device_logs.title')}>
      <PageHeader
        title={t('page.device_logs.title')}
        search={searchInput}
        onClearFilters={() => {
          setSearchInput('')
          setSearch('')
        }}
        onChangeSearch={(query) => {
          setSearchInput(query)
          debouncedSetSearch(query)
        }}
      />
      <Table
        isLoading={isLoading}
        {...tableProps}
        setGlobalFilter={() => {
          setSearchInput('')
          setSearch('')
        }}
      />
    </Page>
  )
}

export default DeviceLogs
